<!--
  GlobalDialog - 전역 다이얼로그 알림창 컴포넌트
  
  사용법:
  ======
  
  1. 기본 사용 (Composable 사용)
  --------------------------------
  import { useDialog } from '@/utils/useDialog';
  
  const { confirm, alert, success, error, warning, info, deleteConfirm } = useDialog();
  
  2. 확인 다이얼로그 (Confirm)
  --------------------------------
  // 간단한 확인
  const result = await confirm('정말 삭제하시겠습니까?');
  if (result) {
    // 확인 클릭 시 처리
  }
  
  3. 알림 다이얼로그 (Alert)
  --------------------------------
  await alert('처리가 완료되었습니다');
  
  4. 간편 메서드
  --------------------------------
  await success('저장되었습니다');
  await error('오류가 발생했습니다');
  await warning('주의가 필요합니다');
  await info('알림 정보입니다');
-->

<script setup lang="ts">
import { useDialogStore } from '@/stores/dialog';
import { computed } from 'vue';
import { AlertCircleIcon, CircleCheckIcon, InfoCircleIcon, HelpIcon, XIcon } from 'vue-tabler-icons';

const dialogStore = useDialogStore();

const icon = computed(() => {
  switch (dialogStore.type) {
    case 'success':
      return CircleCheckIcon;
    case 'error':
      return AlertCircleIcon;
    case 'warning':
      return AlertCircleIcon;
    case 'question':
      return HelpIcon;
    case 'info':
    default:
      return InfoCircleIcon;
  }
});

const iconColor = computed(() => {
  switch (dialogStore.type) {
    case 'success':
      return 'success';
    case 'error':
      return 'error';
    case 'warning':
      return 'warning';
    case 'question':
      return 'primary';
    case 'info':
    default:
      return 'info';
  }
});

const hasCancelButton = computed(() => {
  return dialogStore.cancelText && dialogStore.cancelText.length > 0;
});
</script>

<template>
  <v-dialog
    v-model="dialogStore.show"
    :max-width="dialogStore.width"
    :persistent="dialogStore.persistent"
    @click:outside="dialogStore.close"
  >
    <v-card>
      <v-card-title class="pa-4 d-flex align-center" :class="`bg-${iconColor}`">
        <component :is="icon" size="24" class="mr-2" />
        <span class="text-white">{{ dialogStore.title }}</span>
        <v-spacer></v-spacer>
        <v-btn
          icon
          variant="text"
          size="small"
          @click="dialogStore.close"
          class="text-white"
        >
          <XIcon size="20" />
        </v-btn>
      </v-card-title>
      
      <v-card-text class="pa-6">
        <p class="text-body-1">{{ dialogStore.message }}</p>
      </v-card-text>
      
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn
          v-if="hasCancelButton"
          color="error"
          variant="flat"
          class="px-4 mr-2"
          @click="dialogStore.cancel"
        >
          {{ dialogStore.cancelText }}
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          class="px-4"
          @click="dialogStore.confirm"
        >
          {{ dialogStore.confirmText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
